import { useMemo } from "react";
import {
  type Command,
  useRegisterCommands,
} from "@/components/command-palette";
import { SAMPLE_BATCH_DOCUMENT, SAMPLE_DOCUMENT } from "@/constants";
import { TRANSFORM_EDITOR_ID, usePipelineStaging } from "./pipelineStaging";
import { SCRIPT_TEMPLATES } from "./scriptTemplates";
import { useScriptRun } from "./useScriptRun";

// The new Studio's command-palette entries (⌘K) — old /studio's
// useStudioCommands re-cut for the new UI. The per-kind saves fold into
// one context-aware Save (the ⌘S dispatch); templates and samples land
// in the matching editor after switching its tab; opening pipelines and
// per-row runs keep their toolbar-only homes.
export function StudioCommands() {
  const {
    requestActiveSave,
    requestDraftSave,
    setStageTab,
    setInputDocument,
    setScriptDocument,
    toggleSelected,
    effectiveSelectedId,
  } = usePipelineStaging();
  const { runPipeline } = useScriptRun();

  const commands: Command[] = useMemo(() => {
    const insertScript = (script: string) => {
      setStageTab("transform");
      setScriptDocument(script);
    };
    const setSample = (content: string) => {
      setStageTab("source");
      setInputDocument(content);
    };
    const base: Command[] = [
      {
        id: "studio:run-pipeline",
        label: "Studio: Run pipeline",
        shortcut: "⌘↵",
        action: () => runPipeline(),
      },
      {
        id: "studio:save",
        label: "Studio: Save current edit",
        shortcut: "⌘S",
        action: () => requestActiveSave(),
      },
      {
        id: "studio:save-draft",
        label: "Studio: Save as draft",
        action: () => requestDraftSave(),
      },
      {
        id: "studio:open-pipeline",
        label: "Studio: Open pipeline",
        // GenericDropdownWithSearch has no imperative open — the action
        // clicks the toolbar trigger (its testid is the stable contract),
        // a frame later so the closing palette can't swallow the menu.
        action: () => {
          requestAnimationFrame(() => {
            document
              .querySelector<HTMLButtonElement>(
                '[data-testid="open-pipeline-menu"]',
              )
              ?.click();
          });
        },
      },
      {
        id: "studio:new-script",
        label: "Studio: New script",
        // The pseudo-row's fresh cycle: the empty editor whose next
        // keystroke mints an Untitled script row.
        action: () => {
          setStageTab("transform");
          if (effectiveSelectedId !== TRANSFORM_EDITOR_ID) {
            toggleSelected(TRANSFORM_EDITOR_ID);
          }
        },
      },
      {
        id: "studio:sample-document",
        label: "Studio Sample: Single Document",
        action: () => setSample(SAMPLE_DOCUMENT),
      },
      {
        id: "studio:sample-batch",
        label: "Studio Sample: Batch Documents (3)",
        action: () => setSample(SAMPLE_BATCH_DOCUMENT),
      },
    ];
    const templates: Command[] = SCRIPT_TEMPLATES.map((template) => ({
      id: `studio:template-${template.id}`,
      label: `${template.group === "masking" ? "Studio Mask" : "Studio Transform"}: ${template.label}`,
      action: () => insertScript(template.script),
    }));
    return [...base, ...templates];
  }, [
    requestActiveSave,
    requestDraftSave,
    setStageTab,
    setInputDocument,
    setScriptDocument,
    toggleSelected,
    effectiveSelectedId,
    runPipeline,
  ]);

  useRegisterCommands(commands);
  return null;
}
